const boolish = v => v === true || v === 1 || v === '1' || v === 'true';

const asArray = v => {
  if (v == null) return [];
  return Array.isArray(v) ? v : [];
};

export const formatCommercialAuditWhen = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

export const formatCommercialAuditActor = actor => {
  if (!actor || typeof actor !== 'object') return '—';
  const email = actor.email;
  const name = actor.name?.trim();
  if (email && name) return `${name} (${email})`;
  return email || name || '—';
};

/**
 * Human-readable summary for a commercial_config_audit_log row.
 * @param {object} row - API audit row
 * @param {(tradeId: string) => string} resolveTradeLabel - maps trade UUID to display name (allowed_diy_ai_trades)
 */
export const formatCommercialAuditAction = (row, resolveTradeLabel = id => id) => {
  const { field_name: field, old_value: oldVal, new_value: newVal, metadata } = row || {};

  switch (field) {
    case 'diy_counter_reset': {
      const prior = metadata?.prior_diy_jobs_used ?? oldVal ?? '—';
      let text = `Reset DIY counters (used ${prior} -> 0)`;
      if (metadata?.reason) text += `. ${metadata.reason}`;
      return text;
    }
    case 'diy_job_cap':
      return `Cap ${oldVal ?? '—'} -> ${newVal ?? '—'}`;
    case 'diy_enabled':
      return `DIY ${boolish(oldVal) ? 'on' : 'off'} -> ${boolish(newVal) ? 'on' : 'off'}`;
    case 'allowed_diy_ai_trades': {
      const oldSet = new Set(asArray(oldVal).map(String));
      const newSet = new Set(asArray(newVal).map(String));
      const parts = [];
      for (const id of newSet) {
        if (!oldSet.has(id)) parts.push(`Added trade: ${resolveTradeLabel(id)}`);
      }
      for (const id of oldSet) {
        if (!newSet.has(id)) parts.push(`Removed trade: ${resolveTradeLabel(id)}`);
      }
      return parts.length ? parts.join('; ') : 'Allowed AI trades updated';
    }
    default:
      return field ? `${field}: ${JSON.stringify(oldVal)} -> ${JSON.stringify(newVal)}` : '—';
  }
};

/** DIY Entitlement screen: repeated `field_name` query params for the audit API. */
export const DIY_ENTITLEMENT_AUDIT_FIELD_NAMES = [
  'diy_enabled',
  'diy_job_cap',
  'allowed_diy_ai_trades',
  'diy_counter_reset'
];

export const COMMERCIAL_AUDIT_DEFAULT_PAGE_SIZE = 20;
export const COMMERCIAL_AUDIT_MAX_PAGE_SIZE = 100;

/**
 * Builds query string for GET /api/dashboard/commercial_config_audit_log/
 * @param {object} params
 * @param {string} params.companyEmail — required
 * @param {string[]} [params.fieldNames] — repeated field_name
 * @param {string} [params.since] — ISO-8601 inclusive lower bound
 * @param {string} [params.until] — ISO-8601 inclusive upper bound
 * @param {number} [params.page] — default 1
 * @param {number} [params.pageSize] — default 20, max 100
 */
export const buildCommercialAuditLogQuery = ({
  companyEmail,
  fieldNames = [],
  since,
  until,
  page = 1,
  pageSize = COMMERCIAL_AUDIT_DEFAULT_PAGE_SIZE
}) => {
  const qs = new URLSearchParams();
  qs.set('company_email', String(companyEmail).trim().toLowerCase());
  fieldNames.forEach(name => {
    if (name) qs.append('field_name', name);
  });
  if (since?.trim()) qs.set('since', since.trim());
  if (until?.trim()) qs.set('until', until.trim());
  qs.set('page', String(Math.max(1, Number(page) || 1)));
  const ps = Math.min(
    COMMERCIAL_AUDIT_MAX_PAGE_SIZE,
    Math.max(1, Number(pageSize) || COMMERCIAL_AUDIT_DEFAULT_PAGE_SIZE)
  );
  qs.set('page_size', String(ps));
  return qs.toString();
};
